import { useEffect, useState, type ChangeEvent } from "react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { assertValidNotes, assertValidZone } from "./profileRules";

const NO_DATA_LABEL = "Sin dato";

function toErrorMessage(validate: () => void): string | null {
  try {
    validate();
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : null;
  }
}

interface ProfileTextFieldProps {
  id: string;
  label: string;
  value: string | null;
  readOnly: boolean;
  multiline?: boolean;
  validate: (raw: string | null) => string | null;
  /** Persists the trimmed value (`null` when cleared); rejects to trigger a revert. */
  onCommit: (value: string | null) => Promise<void>;
}

/**
 * A free-text profile field (Zona, Notas del perfil): saved on blur (and on
 * Enter for the single-line Zona field) only when changed and valid, never
 * as the user types. Reverts to the last saved value on a failed save.
 */
function ProfileTextField({
  id,
  label,
  value,
  readOnly,
  multiline = false,
  validate,
  onCommit,
}: ProfileTextFieldProps) {
  const [raw, setRaw] = useState(value ?? "");
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setRaw(value ?? "");
    }
  }, [value, isFocused]);

  async function commit() {
    const trimmed = raw.trim();
    const nextValue = trimmed === "" ? null : trimmed;
    if (nextValue === (value ?? null)) return;

    const validationError = validate(nextValue);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    try {
      await onCommit(nextValue);
    } catch {
      setRaw(value ?? "");
    }
  }

  const handleFocus = () => setIsFocused(true);
  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setRaw(event.target.value);
  const handleBlur = () => {
    setIsFocused(false);
    void commit();
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {readOnly ? (
        <p data-testid={id} className="text-[15px] whitespace-pre-line">
          {value?.trim() ? value : NO_DATA_LABEL}
        </p>
      ) : multiline ? (
        <Textarea
          id={id}
          data-testid={id}
          className="text-[15px]"
          rows={4}
          value={raw}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      ) : (
        <Input
          id={id}
          data-testid={id}
          className="min-h-11 text-base md:text-[15px]"
          value={raw}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
        />
      )}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface ProfileZoneFieldProps {
  value: string | null;
  readOnly: boolean;
  onSave: (value: string | null) => Promise<void>;
  label: string;
}

/** "Zona" (DATA CONTRACT EF (2)): free text, at most 120 chars. */
export function ProfileZoneField({
  value,
  readOnly,
  onSave,
  label,
}: ProfileZoneFieldProps) {
  return (
    <ProfileTextField
      id="profile-zone"
      label={label}
      value={value}
      readOnly={readOnly}
      validate={(raw) => toErrorMessage(() => assertValidZone(raw))}
      onCommit={onSave}
    />
  );
}

export interface ProfileNotesFieldProps {
  value: string | null;
  readOnly: boolean;
  onSave: (value: string | null) => Promise<void>;
  label: string;
}

/** "Notas del perfil" (DATA CONTRACT EF (2)): free text, at most 2000 chars. */
export function ProfileNotesField({
  value,
  readOnly,
  onSave,
  label,
}: ProfileNotesFieldProps) {
  return (
    <ProfileTextField
      id="profile-notes"
      label={label}
      value={value}
      readOnly={readOnly}
      multiline
      validate={(raw) => toErrorMessage(() => assertValidNotes(raw))}
      onCommit={onSave}
    />
  );
}
